'use client';

import { isEmpty } from 'lodash';
import { useSearchParams } from 'next/navigation';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { HebaLoader } from 'components/atomic/atoms';
import { useUIContext } from 'components/context/uiContext/ManagedUiContext';
import StandardLayout from 'components/layout/StandardLayout';
import { getBotpressHebaWABARedirectURL } from 'services/planEventWithHeba.service';

const PlanEventWithHebaWhatsapp = () => {
  const searchParams = useSearchParams();

  const query = useMemo(
    () => Object.fromEntries(searchParams.entries()),
    [searchParams]
  );
  const { isHostIdentityAvailable } = useUIContext();

  const [loading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState({
    message: '',
    show: false,
    status: false
  });

  // Guards against double-redirect when identity resolves while the 5s fallback
  // timer is still pending — both effects call this function but only the first
  // should proceed.
  const redirectInitiated = useRef(false);

  const initiateAndRedirectHebaWABA = useCallback(async () => {
    if (redirectInitiated.current) return;
    redirectInitiated.current = true;
    const initiateWhatsappChatLink = await getBotpressHebaWABARedirectURL({
      query,
      setShowToast
    });
    setLoading(isEmpty(initiateWhatsappChatLink));
    window.location.replace(initiateWhatsappChatLink);
  }, [query, setShowToast]);

  useEffect(() => {
    if (isHostIdentityAvailable) {
      initiateAndRedirectHebaWABA();
    }
  }, [isHostIdentityAvailable, initiateAndRedirectHebaWABA]);

  // Fallback: if identity context hasn't resolved within 5s (slow network, auth
  // failure), proceed anyway — getUserAuth() returns whatever is available.
  // isHostIdentityAvailable is in deps so React cancels the timer (via cleanup)
  // when identity resolves, preventing a double-redirect.
  useEffect(() => {
    if (isHostIdentityAvailable) return;
    const timer = setTimeout(() => {
      initiateAndRedirectHebaWABA();
    }, 5000);
    return () => clearTimeout(timer);
  }, [isHostIdentityAvailable, initiateAndRedirectHebaWABA]);

  return (
    <StandardLayout
      {...{
        setShowToast,
        showHeader: false,
        showLoader: loading,
        showToast
      }}
    >
      <HebaLoader />
    </StandardLayout>
  );
};

export default function PlanEventWithHebaWhatsappClient() {
  return (
    <Suspense fallback={<HebaLoader />}>
      <PlanEventWithHebaWhatsapp />
    </Suspense>
  );
}
